import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';

import StateTsrLayout from '@Components/StateTsrLayout';
import StateTsrTableProps from '@Components/StateTsrTable/StateTsrTableTable.types';
import Table, { TableHead } from '@Components/Table';
import { CommonConstants } from '@Constants/index';
import { Icon } from '@Typings/global.types';
import {
  getRowsPerPageOptions,
  getSortingIcon,
} from '@Utils/common/common.utils';
import query from '@Utils/common/query.utils';
import useRoute from '@Utils/hooks/useRoute.hook';

const StateTsrTable: FunctionComponent<StateTsrTableProps> = (
  props: StateTsrTableProps
): JSX.Element => {
  /** HOOKS */
  const { t } = useTranslation();
  const location = useLocation();
  const { changeRoute } = useRoute();

  /** PROPS */
  const { tableProps, ...resProps } = props;
  const { loading, rows, paginationProps, ...resTableProps } = tableProps;
  const { count, ...resPaginationProps } = paginationProps;

  /** STATES */
  const [page, setPage] = useState<number>(CommonConstants.INITIAL_PAGE_NO - 1);
  const [rowsPerPage, setRowsPerPage] = useState<number>(
    CommonConstants.INITIAL_ROWS_PER_PAGE
  );
  const [searchValue, setSearchValue] = useState<string>('');

  /** Syncs the state according to URL Query Params */
  const syncState = useCallback(() => {
    /** URL PARAMS */
    const stateParam =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.SEARCH) as string) || '';
    const pageNo =
      ((query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) as number) ||
        CommonConstants.INITIAL_PAGE_NO) - 1;
    const pageSize =
      (query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) as number) ||
      CommonConstants.INITIAL_ROWS_PER_PAGE;

    /** SET STATES */
    setPage(pageNo);
    setRowsPerPage(pageSize);
    setSearchValue(stateParam);
  }, [setPage, setRowsPerPage]);

  useEffect(() => {
    syncState();
  }, [syncState, location.search]);

  /** EDGE CASE
   * Append Required URL Params
   */
  useEffect(() => {
    const requiredParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE) ||
        `${CommonConstants.INITIAL_ROWS_PER_PAGE}`,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO) ||
        `${CommonConstants.INITIAL_PAGE_NO}`,
      [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER) ||
        `${CommonConstants.INITIAL_STATE_TSR_SORT_ORDER}`,
      [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
        query.get(CommonConstants.QUERY_PARAMS.COMMON.DIRECTION) ||
        `${CommonConstants.INITIAL_SORT_DIRECTION}`,
    });

    changeRoute(requiredParams);
  }, [changeRoute]);

  /** PAGINATION HANDLERS --> */
  /** ROWS PER PAGE OPTIONS */
  const rowsPerPageOptions = getRowsPerPageOptions(
    CommonConstants.INITIAL_ROWS_PER_PAGE,
    count
  );

  /** HANDLES PAGE CHANGE */
  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ): void => {
    const pageParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO,
      newPage + 1
    );

    changeRoute(pageParam);
  };

  /** HANDLES PAGE SIZE CHANGE */
  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ): void => {
    const newRowsPerPage = parseInt(event.target.value, 10);

    const pageParams = query.setAll({
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_NO]: 1,
      [CommonConstants.QUERY_PARAMS.COMMON.PAGE_SIZE]: newRowsPerPage,
    });

    changeRoute(pageParams);
  };
  /** <-- PAGINATION HANDLERS */

  /** FILTERS --> */
  /** ON SEARCH HANDLER */
  const onSearch = (value: string) => {
    const searchParam = query.set(
      CommonConstants.QUERY_PARAMS.COMMON.SEARCH,
      value
    );
    changeRoute(searchParam);
  };
  /** <-- FILTERS */

  /** TABLE --> */
  /** TABLE HEAD */
  const getTableHead = (): TableHead[] => {
    const orderParam = query.get(CommonConstants.QUERY_PARAMS.COMMON.ORDER);
    const directionParam = query.get(
      CommonConstants.QUERY_PARAMS.COMMON.DIRECTION
    ) as string;

    const handleTableHeadClick = (order: string) => {
      const param = query.setAll({
        [CommonConstants.QUERY_PARAMS.COMMON.ORDER]:
          order || CommonConstants.INITIAL_STATE_TSR_SORT_ORDER,
        ...(order === orderParam
          ? {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                directionParam === CommonConstants.INITIAL_SORT_DIRECTION
                  ? CommonConstants.QUERY_PARAMS.COMMON.SORT_BY.ASC
                  : CommonConstants.INITIAL_SORT_DIRECTION,
            }
          : {
              [CommonConstants.QUERY_PARAMS.COMMON.DIRECTION]:
                CommonConstants.INITIAL_SORT_DIRECTION,
            }),
      });

      changeRoute(param);
    };

    const getTableHeadIcon = (order: string): Icon => {
      if (order === orderParam) {
        return getSortingIcon(directionParam);
      }
      return 'sorting';
    };

    return [
      {
        label: t('common.table.State'),
        icon: getTableHeadIcon(CommonConstants.STATE_TSR.STATE_TSR_SORT),
        width: 22.2,
        onClick: () =>
          handleTableHeadClick(CommonConstants.STATE_TSR.STATE_TSR_SORT),
      },
      {
        label: t('common.table.Weekdays'),
        width: 17.2,
      },
      {
        label: t('common.table.Saturday'),
        width: 17.2,
      },
      {
        label: t('common.table.Sunday'),
        width: 17.2,
      },
      {
        label: t('common.table.Excluded Days'),
        width: 17.2,
      },
      {
        label: t('common.table.Actions'),
        width: 8.5,
        align: 'center',
      },
    ];
  };

  /** STATE TSR TABLE COMPONENT */
  const TableComponent = (
    <Table
      loading={loading}
      rows={rows}
      tableHead={getTableHead()}
      paginationProps={{
        count,
        page,
        rowsPerPage,
        rowsPerPageOptions,
        onPageChange: handleChangePage,
        onRowsPerPageChange: handleChangeRowsPerPage,
        ...resPaginationProps,
      }}
      {...resTableProps}
    />
  );
  /** <-- TABLE */

  return (
    <StateTsrLayout
      searchProps={{
        value: searchValue,
        setValue: setSearchValue,
        placeholder: t('common.tableFilters.Search by state'),
        onSearch,
      }}
      table={TableComponent}
      {...resProps}
    />
  );
};

export default StateTsrTable;
